import { FormEvent, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { postCheckIn } from "./api";
import { ANDROID_BUILD_LABEL, showAndroidBuildStamp } from "./auth/buildStamp";
import { deleteGraphPhoto, putGraphPhoto } from "./auth/token";
import { BoothSearchField } from "./components/BoothSearchField";
import { EVENT_KEY } from "./constants";
import {
  formatBoothHit,
  resolveFloorBooth,
  type BoothSearchHit,
} from "./floor/boothLookup";
import { presenceHasGps } from "./floor/teamColors";
import type { PresenceRecord } from "./types";

const PREVIEW = 280;

function cropToJpeg(
  image: HTMLImageElement,
  zoom: number,
  panX: number,
  panY: number,
): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = 480;
  canvas.height = 480;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    return Promise.reject(new Error("Canvas is not available"));
  }
  const minSide = Math.min(image.naturalWidth, image.naturalHeight);
  const crop = minSide / zoom;
  const maxX = Math.max(0, image.naturalWidth - crop);
  const maxY = Math.max(0, image.naturalHeight - crop);
  const sx = Math.max(0, Math.min(maxX, maxX / 2 + panX * (maxX / 2)));
  const sy = Math.max(0, Math.min(maxY, maxY / 2 + panY * (maxY / 2)));
  ctx.drawImage(image, sx, sy, crop, crop, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error("Could not encode JPEG"));
    }, "image/jpeg", 0.92);
  });
}

export function ProfileScreen({
  displayName,
  email,
  photoUrl,
  presence,
  eventKey,
  campaignKey,
  signedInOid,
  onBack,
  onPhotoChanged,
  onCheckedIn,
}: {
  displayName: string;
  email: string;
  photoUrl: string | null;
  presence?: PresenceRecord | null;
  eventKey: string;
  campaignKey: string;
  signedInOid?: string;
  onBack: () => void;
  onPhotoChanged: () => void;
  onCheckedIn: (hit: BoothSearchHit) => void;
}) {
  const { instance } = useMsal();
  const fileRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [hasDraft, setHasDraft] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [hereQuery, setHereQuery] = useState(() => boothQuery(presence));
  const [checkInBusy, setCheckInBusy] = useState(false);
  const [checkInMessage, setCheckInMessage] = useState("");

  function paint() {
    const canvas = previewRef.current;
    const image = imageRef.current;
    if (!canvas || !image) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    canvas.width = PREVIEW;
    canvas.height = PREVIEW;
    const minSide = Math.min(image.naturalWidth, image.naturalHeight);
    const crop = minSide / zoom;
    const maxX = Math.max(0, image.naturalWidth - crop);
    const maxY = Math.max(0, image.naturalHeight - crop);
    const sx = Math.max(0, Math.min(maxX, maxX / 2 + panX * (maxX / 2)));
    const sy = Math.max(0, Math.min(maxY, maxY / 2 + panY * (maxY / 2)));
    ctx.drawImage(image, sx, sy, crop, crop, 0, 0, PREVIEW, PREVIEW);
  }

  useEffect(() => {
    paint();
  }, [zoom, panX, panY, hasDraft, photoUrl]);

  useEffect(() => {
    if (!presence?.booth_id) return;
    setHereQuery(boothQuery(presence));
  }, [presence?.booth_id, presence?.booth_label, presence?.hall_id]);

  function onFile(file: File | null) {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      imageRef.current = image;
      setZoom(1);
      setPanX(0);
      setPanY(0);
      setHasDraft(true);
      setMessage("");
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      setMessage("That file could not be opened as an image.");
    };
    image.src = url;
  }

  async function saveToGraph() {
    const image = imageRef.current;
    if (!image) {
      setMessage("Choose a photo first.");
      return;
    }
    setBusy(true);
    setMessage("Saving to Microsoft Graph…");
    try {
      const jpeg = await cropToJpeg(image, zoom, panX, panY);
      await putGraphPhoto(instance, jpeg);
      setHasDraft(false);
      imageRef.current = null;
      setMessage("Photo saved to Graph.");
      onPhotoChanged();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Graph photo update failed");
    } finally {
      setBusy(false);
    }
  }

  async function checkInHere(hit: BoothSearchHit) {
    setCheckInBusy(true);
    setCheckInMessage("");
    try {
      await postCheckIn({
        user_id: signedInOid ?? "",
        display_name: displayName.trim() || "Team member",
        event_key: eventKey.trim() || EVENT_KEY,
        hall_id: hit.hall,
        booth_id: hit.booth,
      });
      setHereQuery(formatBoothHit(hit));
      onCheckedIn(hit);
      setCheckInMessage(`Checked in at ${formatBoothHit(hit)}.`);
    } catch (error) {
      setCheckInMessage(error instanceof Error ? error.message : "Check-in failed");
    } finally {
      setCheckInBusy(false);
    }
  }

  async function onCheckInSubmit(event: FormEvent) {
    event.preventDefault();
    const hit = await resolveFloorBooth(eventKey.trim() || EVENT_KEY, hereQuery, campaignKey);
    if (!hit) {
      setCheckInMessage("Enter an account or booth number to check in.");
      return;
    }
    await checkInHere(hit);
  }

  async function removePhoto() {
    setBusy(true);
    setMessage("Removing Graph photo…");
    try {
      await deleteGraphPhoto(instance);
      imageRef.current = null;
      setHasDraft(false);
      setMessage("Graph photo removed.");
      onPhotoChanged();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Graph photo delete failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card profile-screen">
      <div className="profile-screen-header">
        <h2>Profile</h2>
        <button type="button" className="secondary" onClick={onBack}>
          Back
        </button>
      </div>
      <p className="profile-identity">
        <strong>{displayName}</strong>
        {email ? <span className="muted">{email}</span> : null}
      </p>
      <dl className="profile-presence">
        <dt>Presence</dt>
        <dd className={presence?.online ? "ok" : undefined}>{presence?.online ? "Online" : "Offline"}</dd>
        <dt>GPS</dt>
        <dd>{presenceHasGps(presence) ? "On" : "Off"}</dd>
        <dt>Floor coords</dt>
        <dd>{formatCad(presence?.gps_xy)}</dd>
        <dt>GPS coords</dt>
        <dd>{formatLatLng(presence?.gps_latlng)}</dd>
        <dt>I am here</dt>
        <dd>{formatBooth(presence)}</dd>
      </dl>
      <form className="toolbar location-form profile-checkin" onSubmit={(event) => void onCheckInSubmit(event)}>
        <div className="field">
          <label htmlFor="profile-checkin-booth">I am here</label>
          <BoothSearchField
            id="profile-checkin-booth"
            eventKey={eventKey}
            campaignKey={campaignKey}
            value={hereQuery}
            onChange={setHereQuery}
            onPick={(hit) => void checkInHere(hit)}
          />
        </div>
        <div className="actions">
          <button type="submit" disabled={checkInBusy || !hereQuery.trim()}>
            {checkInBusy ? "Checking in…" : "Check in"}
          </button>
        </div>
        {checkInMessage ? <p className="muted">{checkInMessage}</p> : null}
      </form>
      <div className="profile-checkin-log">
        <h3>Check-ins</h3>
        {presence?.check_ins?.length ? (
          <ul>
            {presence.check_ins.map((entry) => (
              <li key={`${entry.booth_id}-${entry.at}`}>
                <strong>
                  {entry.booth_label ? `${entry.booth_label} · ${entry.booth_id}` : entry.booth_id}
                </strong>
                <span className="muted">{formatCheckInAt(entry.at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No booth check-ins recorded yet.</p>
        )}
      </div>
      {showAndroidBuildStamp() ? <p className="muted">{ANDROID_BUILD_LABEL}</p> : null}

      <div className="photo-editor">
        <div className="photo-editor-preview">
          {hasDraft ? (
            <canvas ref={previewRef} className="photo-editor-canvas" width={PREVIEW} height={PREVIEW} />
          ) : photoUrl ? (
            <img className="photo-editor-current" src={photoUrl} alt="" />
          ) : (
            <div className="photo-editor-empty">No Graph photo</div>
          )}
        </div>
        <div className="photo-editor-controls">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(event) => void onFile(event.target.files?.[0] ?? null)}
          />
          <button type="button" className="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>
            Choose photo
          </button>
          {hasDraft ? (
            <>
              <label className="photo-slider">
                Zoom
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(event) => setZoom(Number(event.target.value))}
                />
              </label>
              <label className="photo-slider">
                Horizontal
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.02}
                  value={panX}
                  onChange={(event) => setPanX(Number(event.target.value))}
                />
              </label>
              <label className="photo-slider">
                Vertical
                <input
                  type="range"
                  min={-1}
                  max={1}
                  step={0.02}
                  value={panY}
                  onChange={(event) => setPanY(Number(event.target.value))}
                />
              </label>
            </>
          ) : null}
          <div className="actions">
            <button type="button" disabled={busy || !hasDraft} onClick={() => void saveToGraph()}>
              Save to Graph
            </button>
            <button type="button" className="secondary" disabled={busy} onClick={() => void removePhoto()}>
              Remove
            </button>
          </div>
          {message ? <p className="muted">{message}</p> : null}
        </div>
      </div>
    </section>
  );
}

function formatCad(xy?: [number, number] | null): string {
  if (!xy) return "—";
  return `${Math.round(xy[0])}, ${Math.round(xy[1])}`;
}

function formatLatLng(latlng?: [number, number] | null): string {
  if (!latlng) return "—";
  return `${latlng[0].toFixed(5)}, ${latlng[1].toFixed(5)}`;
}

function formatBooth(presence?: PresenceRecord | null): string {
  if (!presence?.booth_id) return "—";
  return presence.booth_label ? `${presence.booth_label} · ${presence.booth_id}` : presence.booth_id;
}

function boothQuery(presence?: PresenceRecord | null): string {
  if (!presence?.booth_id) return "";
  return presence.booth_label
    ? formatBoothHit({ booth: presence.booth_id, label: presence.booth_label, hall: presence.hall_id ?? undefined })
    : presence.booth_id;
}

function formatCheckInAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
